import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Link,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { themeVars } from '@mds/ui';
import { Link as RouterLink } from 'react-router-dom';
import { ChannelCoverImage } from '../../../../ui/ChannelCoverImage';
import {
    USER_GUIDE_BANNER_DESCRIPTION,
    USER_GUIDE_BANNER_IMAGE,
    USER_GUIDE_BANNER_PUBLISHER,
} from '../constants';

interface UserGuideBannerProps {
    publishedByDeskLink?: string;
}

const level1Alpha = (theme: Theme, opacity: number) =>
    theme.alpha(themeVars(theme).palette.background.paper, opacity);

const UserGuideBanner: React.FC<UserGuideBannerProps> = ({
    publishedByDeskLink,
}) => {
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Card
            variant="outlined"
            id="user-guide-cover"
            sx={{ overflow: 'hidden', bgcolor: 'background.level1' }}
        >
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-end',
                    height: compact ? 124 : 250,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component={ChannelCoverImage}
                    src={USER_GUIDE_BANNER_IMAGE}
                    alt=""
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        alignItems: compact ? 'flex-end' : 'center',
                        zIndex: 1,
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        height: compact ? 124 : 104,

                        background: (bannerTheme) =>
                            `linear-gradient(180deg, ${level1Alpha(bannerTheme, 0.08)} 0%, ${level1Alpha(bannerTheme, 0.72)} 42%, ${level1Alpha(bannerTheme, 0.9)} 100%)`,

                        p: compact ? 2 : 4,
                    }}
                >
                    <Stack
                        spacing={0.5}
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Typography
                            variant={compact ? 'h5' : 'h3'}
                            sx={{
                                color: 'text.primary',
                            }}
                        >
                            Neon User Guides
                        </Typography>
                        <Typography
                            variant={compact ? 'caption' : 'body1'}
                            sx={{
                                color: 'text.primary',
                            }}
                        >
                            Published by{' '}
                            {publishedByDeskLink ? (
                                <Link
                                    component={RouterLink}
                                    to={publishedByDeskLink}
                                    underline="hover"
                                    color="primary"
                                >
                                    <strong>
                                        {USER_GUIDE_BANNER_PUBLISHER}
                                    </strong>
                                </Link>
                            ) : (
                                <strong>{USER_GUIDE_BANNER_PUBLISHER}</strong>
                            )}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
            <CardContent
                sx={{
                    bgcolor: 'background.level1',
                    p: compact ? 2 : 4,
                    '&:last-child': {
                        pb: compact ? 2 : 4,
                    },
                }}
            >
                <Typography
                    variant={compact ? 'body1' : 'subtitle1'}
                    sx={{
                        color: 'text.secondary',
                        maxWidth: 800,
                    }}
                >
                    {USER_GUIDE_BANNER_DESCRIPTION}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default UserGuideBanner;
